import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Skeleton, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import InfoPopover from './listvariable';
import { themeExpert } from '../../theme/expert';

const OUTILS = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const vide = (html) => !html || html === '<p><br></p>';

/** Les modèles de courrier, écrits une fois et remplis par dossier. */
function PDFFiles() {
  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/templates/');
      setModeles(data.results || data);
      setErreur('');
    } catch {
      setErreur("Les modèles n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = new FormData();
      corps.append('title', fiche.title.trim());
      corps.append('content', fiche.content);
      if (fiche.id) {
        await axiosInstance.patch(`/templates/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/templates/', corps);
      }
      setFiche(null);
      setMessage('Modèle enregistré');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/templates/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Modèle supprimé');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Modèles de courrier"
        explication="Depuis une tâche de type courriel, choisir un modèle en remplit le corps avec les données du dossier."
        actions={(
          <Stack direction="row" spacing={1.5}>
            <InfoPopover />
            <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                    onClick={() => setFiche({ title: '', content: '' })}>
              Nouveau modèle
            </Button>
          </Stack>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && modeles.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucun modèle pour l'instant.
          </Typography>
        )}

        {!chargement && modeles.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modèle</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modeles.map((modele) => (
                  <TableRow key={modele.id} hover>
                    <TableCell>{modele.title}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => setFiche({
                                      ...modele,
                                      content: modele.content || '' })}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(modele)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="lg">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier le modèle' : 'Nouveau modèle de courrier'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Titre du modèle" value={fiche?.title || ''}
                           onChange={(e) => setFiche(
                             (s) => ({ ...s, title: e.target.value }))}
                           required autoFocus
                           helperText="Ex. : courrier de suivi après rendez-vous." />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Corps du courrier
                  </Typography>
                  {/* La hauteur de Quill se contraint ici. Une feuille de
                      style globale la posait pour toute l'application, avec
                      au passage un dégradé violet sur chaque fenêtre. */}
                  <Box sx={{ '& .ql-container': { minHeight: 340 },
                             '& .ql-editor': { minHeight: 340 } }}>
                    <ReactQuill
                      theme="snow"
                      value={fiche?.content || ''}
                      onChange={(html) => setFiche(
                        (s) => ({ ...s, content: html }))}
                      modules={OUTILS}
                      placeholder="Écrivez le courrier, avec les variables entre accolades…"
                    />
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.title?.trim()
                                || vide(fiche?.content)}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        {/* `window.confirm` servait de garde-fou : une fenêtre du navigateur,
            qui ne dit pas ce qu'elle emporte. */}
        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer ce modèle ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » ne sera plus proposé. Les courriers déjà
              écrits à partir de lui ne bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default PDFFiles;
